/**
 * Touch as an input device: whether this machine is one, and what the thumbs
 * are actually doing.
 *
 * Two halves live here and neither of them draws anything. The first decides
 * whether the on-screen controls should exist at all — see `touchPreferred`,
 * which is the gate the whole feature hangs off. The second is `TouchModel`, a
 * DOM-free control model: it is handed pointer positions in CSS pixels and it
 * writes axes, look and actions into an `InputSink`. Those are the same numbers
 * the keyboard and mouse write, so nothing downstream can tell where a frame's
 * input came from, and `core/input.ts` needs no new public surface for it.
 *
 * `ui/touchhud.ts` is the other half: the elements, the pointer plumbing and
 * the drawing. It is loaded on demand and a desktop never fetches it.
 *
 * The feel here is deliberately not a shooter's. The game is slow, the rooms
 * are large and most of a run is walking, so look is geared low, the stick has
 * a wide deadzone, and running is something you lean into rather than a button
 * you hold — a held sprint costs a finger the player does not have spare.
 */

import { TUNING } from './config';
import type { InputSink } from './input';

/**
 * Feel constants for the on-screen controls. See `TUNING.touch`, which is
 * where they live; this is the local name the rest of the file reads them by.
 */
const TOUCH = TUNING.touch;

/* ------------------------------------------------------------------ *
 * Detection
 * ------------------------------------------------------------------ */

/** A screen that can be touched at all. Says nothing about what it is used with. */
export function hasTouchHardware(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  if (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0) return true;
  return 'ontouchstart' in window;
}

/**
 * Whether this browser has the Pointer Lock API at all.
 *
 * On iOS Safari it is absent rather than restricted — there is no
 * `requestPointerLock` to call and no error to catch — which is why the touch
 * path may never depend on it and why its absence is itself strong evidence
 * that the only way to look around here is a finger.
 */
export function hasPointerLock(): boolean {
  if (typeof document === 'undefined' || typeof Element === 'undefined') return false;
  return (
    typeof document.exitPointerLock === 'function' &&
    typeof Element.prototype.requestPointerLock === 'function'
  );
}

/** True when a mouse, trackpad or stylus is attached *as well as* the screen. */
function hasFinePointer(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(any-pointer: fine)').matches;
}

/**
 * `?touch=1` / `?touch=0`, or null when the query string says nothing.
 *
 * The only way to see these controls on a machine that would not otherwise get
 * them, which is how they can be looked at without an iPad in the room. Opt-in
 * both ways and never sticky — nothing writes it, so a reload without the
 * parameter is back to the ordinary decision.
 */
export function forcedTouchMode(): boolean | null {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get('touch');
    if (value === null) return null;
    return value !== '0' && value !== 'false';
  } catch {
    return null;
  }
}

/**
 * Should this machine be played with thumbs?
 *
 * Capability, never the user-agent string. Touch has to be present *and* there
 * has to be no fine pointer: a Windows laptop with a touchscreen has a
 * trackpad, pointer lock and a keyboard, and taking the desktop controls away
 * from it would be a regression on a machine that was never broken.
 *
 * The one case that overrides the fine-pointer test is a browser with no
 * Pointer Lock API. There, a mouse cannot drive the camera either, so the
 * on-screen controls are not a preference — they are the only way to look
 * around at all.
 */
export function touchPreferred(): boolean {
  const forced = forcedTouchMode();
  if (forced !== null) return forced;
  if (!hasTouchHardware()) return false;
  if (!hasPointerLock()) return true;
  return !hasFinePointer();
}

/**
 * Call back whenever the answer to `touchPreferred` might have changed.
 *
 * A Magic Keyboard being clipped on or off an iPad is a live media-query
 * change, not a reload, and the controls have to follow it. Returns an
 * unsubscribe. Installs nothing on a browser without `matchMedia`.
 */
export function watchTouchPreference(fn: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const lists = ['(any-pointer: fine)', '(pointer: coarse)'].map((q) => window.matchMedia(q));
  const handler = (): void => fn();
  for (const list of lists) {
    // `addListener` is the deprecated spelling, and the only one Safari
    // understood until 14. Cheap to keep, and this is the file that runs there.
    if (typeof list.addEventListener === 'function') list.addEventListener('change', handler);
    else if (typeof list.addListener === 'function') list.addListener(handler);
  }
  return () => {
    for (const list of lists) {
      if (typeof list.removeEventListener === 'function') list.removeEventListener('change', handler);
      else if (typeof list.removeListener === 'function') list.removeListener(handler);
    }
  };
}

/** Stick travel for this screen, in CSS pixels. Re-measured on every press. */
export function stickRadius(shortEdge: number): number {
  return Math.max(
    TOUCH.stickMinRadius,
    Math.min(TOUCH.stickMaxRadius, shortEdge * TOUCH.stickRadiusFraction)
  );
}

/* ------------------------------------------------------------------ *
 * The control model
 * ------------------------------------------------------------------ */

/**
 * Where the stick should be drawn. Read by the HUD every time it moves;
 * mutated in place so a drag allocates nothing.
 */
export interface StickView {
  /** True while a finger is on it. The ring is invisible otherwise. */
  active: boolean;
  /** Centre of travel, in CSS pixels relative to the viewport. */
  originX: number;
  originY: number;
  /** Where the finger is. Never further than `radius` from the origin. */
  tipX: number;
  tipY: number;
  radius: number;
  /** 0..1 of full travel. Drives the run indication. */
  mag: number;
  /** True while the push is deep enough to be a run. */
  running: boolean;
}

/** One live look drag. Only the previous position is needed; deltas are what count. */
interface LookDrag {
  x: number;
  y: number;
}

/**
 * The thumbs, as movement and look.
 *
 * Pure: it is given positions and it writes to the sink. No element is read,
 * no rect is measured and no frame hook is needed — pointer events land
 * between frames, so a delta accumulated the moment it arrives is exactly the
 * mouse's own contract.
 */
export class TouchModel {
  readonly stick: StickView = {
    active: false,
    originX: 0,
    originY: 0,
    tipX: 0,
    tipY: 0,
    radius: TOUCH.stickMinRadius,
    mag: 0,
    running: false,
  };

  /** Pointer id driving the stick, or -1. One thumb, one stick. */
  private stickId = -1;
  /** Every other finger currently dragging the camera, by pointer id. */
  private readonly looks = new Map<number, LookDrag>();
  private crouched = false;

  constructor(private readonly sink: InputSink) {}

  /** True while the crouch latch is held. The button draws itself from this. */
  get crouching(): boolean {
    return this.crouched;
  }

  /** Put the stick down at `x, y`. Ignored while another finger already has it. */
  beginStick(id: number, x: number, y: number, radius: number): boolean {
    if (this.stickId >= 0) return false;
    const s = this.stick;
    this.stickId = id;
    s.active = true;
    s.originX = x;
    s.originY = y;
    s.tipX = x;
    s.tipY = y;
    s.radius = radius;
    s.mag = 0;
    s.running = false;
    this.sink.axes(0, 0);
    return true;
  }

  /** Start a camera drag. Any number of these may be live at once. */
  beginLook(id: number, x: number, y: number): void {
    this.looks.set(id, { x, y });
  }

  /** A finger moved. Routed by pointer id; unknown ids are somebody else's. */
  move(id: number, x: number, y: number): void {
    if (id === this.stickId) {
      this.dragStick(x, y);
      return;
    }
    const last = this.looks.get(id);
    if (!last) return;
    const dx = x - last.x;
    const dy = y - last.y;
    last.x = x;
    last.y = y;
    // Radians, exactly as the mouse produces them, so the sensitivity setting
    // and invert-Y keep meaning what they mean everywhere else.
    const scale = this.sink.lookScale * TOUCH.lookGain;
    this.sink.look(dx * scale, dy * scale * TOUCH.pitchGain);
  }

  /** A finger lifted or was cancelled. */
  end(id: number): void {
    if (id === this.stickId) {
      this.releaseStick();
      return;
    }
    this.looks.delete(id);
  }

  /**
   * Drop everything held. Called when the window loses focus and when the
   * controls are torn down — a latch left set while the keyboard path clears
   * `down` underneath it is a player permanently crouched.
   */
  releaseAll(): void {
    this.releaseStick();
    this.looks.clear();
    this.setCrouch(false);
  }

  /** The crouch button. A latch, because it has to be usable one-handed. */
  toggleCrouch(): boolean {
    this.setCrouch(!this.crouched);
    return this.crouched;
  }

  private setCrouch(on: boolean): void {
    if (on === this.crouched) return;
    this.crouched = on;
    this.sink.hold('crouch', on);
  }

  private releaseStick(): void {
    if (!this.stick.active) return;
    this.stickId = -1;
    this.stick.active = false;
    this.stick.mag = 0;
    this.sink.axes(0, 0);
    this.setRunning(false);
  }

  private setRunning(on: boolean): void {
    if (on === this.stick.running) return;
    this.stick.running = on;
    this.sink.hold('sprint', on);
  }

  private dragStick(x: number, y: number): void {
    const s = this.stick;
    let dx = x - s.originX;
    let dy = y - s.originY;
    const len = Math.hypot(dx, dy);
    if (len > s.radius) {
      // The centre follows a finger that has run out of travel, so a long drag
      // keeps steering instead of pinning at the rim of wherever it started.
      const pull = (len - s.radius) / len;
      s.originX += dx * pull;
      s.originY += dy * pull;
      dx = x - s.originX;
      dy = y - s.originY;
    }
    s.tipX = x;
    s.tipY = y;

    const nx = dx / s.radius;
    const ny = dy / s.radius;
    const mag = Math.min(1, Math.hypot(nx, ny));
    s.mag = mag;

    if (mag <= TOUCH.deadzone) {
      this.sink.axes(0, 0);
      this.setRunning(false);
      return;
    }
    // Rescaled so the edge of the deadzone is a standing start rather than a
    // jump to a fifth of full push.
    const gain = (mag - TOUCH.deadzone) / (1 - TOUCH.deadzone) / mag;
    // Screen Y grows downward; +moveZ is forward.
    this.sink.axes(nx * gain, -ny * gain);
    // Hysteresis, so a thumb resting on the rim does not stutter in and out of
    // a run — and with it, in and out of the noise that gets you killed.
    this.setRunning(mag >= (s.running ? TOUCH.sprintOff : TOUCH.sprintOn));
  }
}
